// Authentication service using Firebase Auth

import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  UserCredential,
  updateProfile
} from 'firebase/auth';

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

export interface User {
  uid: string;
  name: string;
  email: string;
}

export interface SignUpData {
  name: string;
  email: string;
  password: string;
}

export interface SignInData {
  email: string;
  password: string;
}

class AuthService {
  private user: User | null = null;
  private authStateListeners: ((user: User | null) => void)[] = [];
  private isInitialized: boolean = false;
  private initListeners: ((initialized: boolean) => void)[] = [];

  constructor() {
    // Set up auth state listener
    onAuthStateChanged(auth, (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        this.user = {
          uid: firebaseUser.uid,
          name: firebaseUser.displayName || '',
          email: firebaseUser.email || ''
        };
      } else {
        this.user = null;
      }
      
      // Mark as initialized after first auth state change
      if (!this.isInitialized) {
        this.isInitialized = true;
        this.initListeners.forEach(listener => listener(true));
      }
      
      // Notify all listeners
      this.authStateListeners.forEach(listener => listener(this.user));
    });
  }

  // Wait for Firebase Auth to initialize
  async waitForInitialization(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    
    return new Promise((resolve) => {
      const listener = (initialized: boolean) => {
        if (initialized) {
          resolve();
          // Remove this listener
          const index = this.initListeners.indexOf(listener);
          if (index > -1) {
            this.initListeners.splice(index, 1);
          }
        }
      };
      this.initListeners.push(listener);
    });
  }

  // Check if Firebase Auth has initialized
  isAuthInitialized(): boolean {
    return this.isInitialized;
  }

  async signUp(data: SignUpData): Promise<User> {
    try {
      const userCredential: UserCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );

      // Update display name
      if (userCredential.user) {
        await updateProfile(userCredential.user, {
          displayName: data.name
        });
      }

      return {
        uid: userCredential.user.uid,
        name: data.name,
        email: data.email
      };
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  }

  async signIn(data: SignInData): Promise<User> {
    try {
      const userCredential: UserCredential = await signInWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );

      return {
        uid: userCredential.user.uid,
        name: userCredential.user.displayName || '',
        email: userCredential.user.email || ''
      };
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }

  async getCurrentUser(): Promise<User> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }

    return {
      uid: currentUser.uid,
      name: currentUser.displayName || '',
      email: currentUser.email || ''
    };
  }

  async getAuthToken(): Promise<string | null> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return null;
    }

    try {
      return await currentUser.getIdToken();
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }

  signOut(): void {
    firebaseSignOut(auth);
  }

  isAuthenticated(): boolean {
    return auth.currentUser !== null;
  }

  getUser(): User | null {
    return this.user;
  }

  // Add listener for auth state changes
  onAuthStateChanged(listener: (user: User | null) => void): () => void {
    this.authStateListeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.authStateListeners.indexOf(listener);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }
}

// Utility function to convert Firebase error codes to user-friendly messages
function isFirebaseAuthError(e: unknown): e is { code: string } {
  return typeof e === 'object' && e !== null && typeof (e as { code?: unknown }).code === 'string';
}

export function getFirebaseErrorMessage(error: unknown): string {
  if (!isFirebaseAuthError(error)) {
    return 'An unexpected error occurred. Please try again.';
  }

  switch (error.code) {
    case 'auth/invalid-credential':
      return 'Invalid email or password. Please check your credentials and try again.';
    case 'auth/user-not-found':
      return 'No account found with this email address. Please check your email or sign up.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Please sign in instead.';
    case 'auth/weak-password':
      return 'Password is too weak. Please choose a stronger password (at least 6 characters).';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please wait a moment before trying again.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection and try again.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled. Please contact support.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different sign-in method.';
    case 'auth/invalid-password':
      return 'Invalid password format. Please check your password and try again.';
    case 'auth/user-mismatch':
      return 'The provided credentials do not correspond to the previously signed in user.';
    case 'auth/requires-recent-login':
      return 'This operation requires recent authentication. Please sign in again.';
    case 'auth/credential-already-in-use':
      return 'This credential is already associated with a different user account.';
    default:
      return 'An error occurred during authentication. Please try again.';
  }
}

// Export singleton instance
export const authService = new AuthService(); 